import { useEffect, useState } from "react";
import { toast } from "sonner";
import { NotebookPen, Plus, Pencil, Trash2, TrendingUp, TrendingDown, Search, ImageIcon, ChevronDown, FileDown } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { JournalEntryDialog } from "./JournalEntryDialog";

const money = (n) => (n === null || n === undefined ? "—" : `${n < 0 ? "-" : ""}₹${Math.abs(n).toLocaleString("en-IN")}`);
const RESULT_BADGE = {
  win: "bg-emerald-100 text-emerald-700", loss: "bg-red-100 text-red-600",
  breakeven: "bg-slate-100 text-slate-600", open: "bg-amber-100 text-amber-700",
};

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 px-4 py-3">
      <p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`font-display text-xl font-bold mt-1 ${tone || "text-white"}`}>{value}</p>
    </div>
  );
}

function EntryCard({ e, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(false);
  const hasReasons = e.reason_entry || e.reason_exit || e.mistakes || e.notes;
  return (
    <div className="bg-white border border-border rounded-lg p-5" data-testid={`journal-entry-${e.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-lg font-bold text-navy">{e.symbol}</span>
            <span className={`text-[11px] font-semibold uppercase px-2 py-0.5 rounded-full ${e.direction === "long" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>{e.direction}</span>
            <span className="text-[11px] uppercase text-slate-400">{e.segment}</span>
            <span className="text-xs text-slate-400">· {e.date}</span>
          </div>
          {e.setup && <p className="text-sm text-slate-500 mt-1">Setup: {e.setup}</p>}
        </div>
        <div className="text-right shrink-0">
          <p className={`font-display text-lg font-bold ${e.pnl > 0 ? "text-emerald-600" : e.pnl < 0 ? "text-red-600" : "text-slate-500"}`}>{money(e.pnl)}</p>
          <span className={`inline-block mt-1 text-[11px] font-semibold uppercase px-2 py-0.5 rounded-full ${RESULT_BADGE[e.result] || RESULT_BADGE.open}`}>{e.result}</span>
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-4 text-sm">
        {[["Entry", e.entry_price], ["Exit", e.exit_price], ["Qty", e.quantity], ["SL", e.stop_loss], ["Target", e.target]].map(([l, v]) => (
          <div key={l} className="rounded-md bg-subtle px-2 py-1.5"><span className="text-[10px] uppercase text-slate-400 block">{l}</span><span className="text-navy font-medium">{v ?? "—"}</span></div>
        ))}
      </div>

      {(e.tags?.length > 0 || e.emotion) && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {e.emotion && <span className="text-[11px] px-2 py-0.5 rounded-full bg-gold/15 text-gold">{e.emotion}</span>}
          {(e.tags || []).map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-navy/5 text-navy">#{t}</span>)}
        </div>
      )}

      {(hasReasons || e.chart_image) && (
        <button onClick={() => setOpen((o) => !o)} data-testid={`journal-toggle-${e.id}`} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-navy hover:text-gold transition-colors">
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Hide" : "Details & reasons"}
        </button>
      )}
      {open && (
        <div className="mt-3 space-y-2 text-sm border-t border-border pt-3">
          {e.reason_entry && <p><span className="text-slate-400">Entry reason: </span><span className="text-slate-700">{e.reason_entry}</span></p>}
          {e.reason_exit && <p><span className="text-slate-400">Exit reason: </span><span className="text-slate-700">{e.reason_exit}</span></p>}
          {e.mistakes && <p><span className="text-slate-400">Mistakes/Lessons: </span><span className="text-slate-700">{e.mistakes}</span></p>}
          {e.notes && <p><span className="text-slate-400">Notes: </span><span className="text-slate-700">{e.notes}</span></p>}
          {e.chart_image && <img src={e.chart_image} alt="chart" onClick={() => setZoom(true)} className="mt-2 max-h-56 rounded-md border border-border cursor-zoom-in" data-testid={`journal-chart-${e.id}`} />}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button onClick={() => onEdit(e)} data-testid={`journal-edit-${e.id}`} className="h-8 px-3 rounded-md border border-border text-sm text-slate-600 hover:text-navy inline-flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Edit</button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid={`journal-delete-${e.id}`} className="h-8 px-3 rounded-md border border-red-200 text-sm text-red-500 hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete this entry?</AlertDialogTitle><AlertDialogDescription>This trade journal entry will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => onDelete(e.id)} data-testid={`journal-delete-confirm-${e.id}`} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="sm:max-w-3xl"><DialogTitle className="sr-only">Trade chart</DialogTitle><img src={e.chart_image} alt="chart" className="w-full rounded-md" /></DialogContent>
      </Dialog>
    </div>
  );
}

export function TradingJournal({ onLoaded }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [symbolQ, setSymbolQ] = useState("");
  const [resultQ, setResultQ] = useState("all");

  const load = () => api.get("/me/journal").then((r) => { setEntries(r.data); onLoaded?.(r.data.length); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e); setDialogOpen(true); };
  const del = async (id) => { try { await api.delete(`/me/journal/${id}`); toast.success("Entry deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const exportCsv = () => {
    if (!entries.length) return;
    const cols = [["date", "Date"], ["symbol", "Symbol"], ["direction", "Direction"], ["segment", "Segment"], ["entry_price", "Entry"], ["exit_price", "Exit"], ["quantity", "Quantity"], ["stop_loss", "StopLoss"], ["target", "Target"], ["pnl", "PnL"], ["result", "Result"], ["setup", "Setup"], ["emotion", "Emotion"], ["reason_entry", "ReasonForEntry"], ["reason_exit", "ReasonForExit"], ["mistakes", "MistakesLessons"], ["notes", "Notes"]];
    const esc = (v) => { if (v === null || v === undefined) v = ""; if (Array.isArray(v)) v = v.join(" "); v = String(v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
    const header = cols.map((c) => c[1]).concat("Tags").join(",");
    const rows = entries.map((e) => cols.map((c) => esc(e[c[0]])).concat(esc((e.tags || []).map((t) => "#" + t).join(" "))).join(","));
    const blob = new Blob(["\uFEFF" + [header, ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `krvprime-trading-journal-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Journal exported to CSV");
  };

  const filtered = entries.filter((e) =>
    (symbolQ ? (e.symbol || "").toLowerCase().includes(symbolQ.toLowerCase()) : true) &&
    (resultQ === "all" ? true : e.result === resultQ)
  );

  const closed = entries.filter((e) => e.result !== "open" && e.pnl !== null);
  const wins = closed.filter((e) => e.pnl > 0).length;
  const losses = closed.filter((e) => e.pnl < 0).length;
  const winRate = wins + losses > 0 ? Math.round((wins / (wins + losses)) * 100) : 0;
  const totalPnl = closed.reduce((a, e) => a + (e.pnl || 0), 0);
  const best = closed.length ? Math.max(...closed.map((e) => e.pnl)) : null;
  const worst = closed.length ? Math.min(...closed.map((e) => e.pnl)) : null;

  return (
    <div id="trading-journal" data-testid="trading-journal">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl font-bold text-navy flex items-center gap-2"><NotebookPen className="h-6 w-6 text-gold" /> My Trading Journal</h2>
        <div className="flex gap-2">
          <button onClick={exportCsv} disabled={!entries.length} data-testid="journal-export-btn" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md border border-border text-sm font-semibold text-navy hover:border-gold/50 transition-colors disabled:opacity-50"><FileDown className="h-4 w-4" /> Export CSV</button>
          <button onClick={openNew} data-testid="journal-add-btn" className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors"><Plus className="h-4 w-4" /> Log trade</button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="bg-navy grain rounded-lg p-5 grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="journal-summary">
        <Stat label="Trades" value={entries.length} />
        <Stat label="Win rate" value={`${winRate}%`} tone="text-gold" />
        <Stat label="Net P&L" value={money(totalPnl)} tone={totalPnl >= 0 ? "text-emerald-400" : "text-red-400"} />
        <Stat label="Best" value={money(best)} tone="text-emerald-400" />
        <Stat label="Worst" value={money(worst)} tone="text-red-400" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Filter by symbol…" value={symbolQ} onChange={(e) => setSymbolQ(e.target.value)} className="pl-9" data-testid="journal-filter-symbol" />
        </div>
        <Select value={resultQ} onValueChange={setResultQ}>
          <SelectTrigger className="sm:w-48" data-testid="journal-filter-result"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All results</SelectItem>
            <SelectItem value="win">Wins</SelectItem>
            <SelectItem value="loss">Losses</SelectItem>
            <SelectItem value="breakeven">Break-even</SelectItem>
            <SelectItem value="open">Open</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-slate-400">Loading journal…</p>
      ) : entries.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="journal-empty">
          <NotebookPen className="h-10 w-10 text-slate-300 mx-auto" />
          <h3 className="font-display text-xl font-semibold text-navy mt-4">Start your trading journal</h3>
          <p className="text-slate-500 mt-2">Log every trade with your reasons, mistakes and emotions to build discipline over time.</p>
          <button onClick={openNew} data-testid="journal-empty-add" className="inline-flex items-center gap-2 mt-6 h-11 px-6 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors"><Plus className="h-4 w-4" /> Log your first trade</button>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-slate-500 py-8 text-center">No entries match your filters.</p>
      ) : (
        <div className="space-y-4">
          {filtered.map((e) => <EntryCard key={e.id} e={e} onEdit={openEdit} onDelete={del} />)}
        </div>
      )}

      <JournalEntryDialog entry={editing} open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
    </div>
  );
}
